import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCodingRoutes } from '../../hooks/useCodingRoutes';
import useBatchStore from '../../store/batchStore';
import useAssignmentStore from '../../store/assignmentStore';
import {
  Box, Button, Typography, Chip, CircularProgress, Alert, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
  LinearProgress,
} from '@mui/material';
import GlassCard from '../../components/Cards/GlassCard';
import { DeleteRounded, HourglassBottomOutlined, VisibilityRounded } from '@mui/icons-material';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import AppBreadcrumbs from '../../components/AppBreadcrumbs';
import CommonDataGrid from '../../components/CodingTracker/CommonDataGrid';
import {
  surfaceGradientSx, gradientButtonSx, gradientTextSx, primaryChipSx,
  gridHeaderBgSx, gridRowHoverSx, gridRowStripeSx,
  PRIMARY, SECONDARY,
} from '../../utils/colors';
import { getDataGridGlobalSerialNumber } from '../../utils/dataGridSerialNumber';

// ── Add Assignment Modal ──────────────────────────────────────────────────────
const AddAssignmentModal = ({ onClose, onSubmit }) => {
  const [form, setForm] = useState({ title: '', description: '', dueDate: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return alert('Title is required');
    try {
      setSubmitting(true);
      await onSubmit(form);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 4 } }}>
      <DialogTitle sx={{ fontWeight: 800, pb: 0 }}>Create Assignment</DialogTitle>
      <Box component="form" onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Title" name="title" value={form.title} onChange={handleChange}
            placeholder="e.g. Arrays & Strings" required fullWidth size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField label="Description" name="description" value={form.description}
            onChange={handleChange} placeholder="Optional description…"
            multiline rows={3} fullWidth size="small"
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
          <TextField label="Due Date" name="dueDate" type="date" value={form.dueDate}
            onChange={handleChange} fullWidth size="small" InputLabelProps={{ shrink: true }}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', borderRadius: 2 }}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={submitting}
            sx={{ ...gradientButtonSx, px: 3 }}>
            {submitting ? <CircularProgress size={18} /> : 'Create'}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

// ── Assignment Page ───────────────────────────────────────────────────────────
const AssignmentPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const cr = useCodingRoutes();
  const [showModal, setShowModal] = useState(false);

  const { currentBatch, fetchBatchById } = useBatchStore();
  const {
    // eslint-disable-next-line no-unused-vars
    assignments, studentCount, loading, error, deleting,
    fetchAssignments, addAssignment, removeAssignment,
  } = useAssignmentStore();

  useEffect(() => {
    fetchBatchById(batchId);
    fetchAssignments(batchId);
  }, [batchId, fetchBatchById, fetchAssignments]);

  const handleCreate = async (form) => {
    try {
      await addAssignment(batchId, form);
      setShowModal(false);
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  const handleDelete = async (assignmentId) => {
    if (!window.confirm('Delete this assignment and all its problems?')) return;
    try {
      await removeAssignment(batchId, assignmentId);
    } catch (e) {
      alert(e?.response?.data?.message || e.message);
    }
  };

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const isDue = (dueDate) => dueDate && new Date(dueDate) < new Date();

  const columns = [
    {
      field: 'index', headerName: '#', width: 60, sortable: false,
      renderCell: (params) => (
        <Typography sx={{ paddingTop: 1 }}>{getDataGridGlobalSerialNumber(params)}</Typography>
      ),
    },
    {
      field: 'title', headerName: 'Assignment Title', flex: 2, minWidth: 160,
      renderCell: (params) => (<Typography fontWeight={600} onClick={() => navigate(cr.assignment(batchId, params.row._id))} sx={{ cursor: 'pointer', paddingTop: 2 }}>{params.value}</Typography>),
    },
    {
      field: 'description', headerName: 'Description', flex: 2, minWidth: 140,
      renderCell: (params) => <Typography variant="body2" color="text.secondary" sx={{ paddingTop: 2 }}>{params.value || '—'}</Typography>,
    },
    {
      field: 'dueDate', headerName: 'Due Date', width: 160,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Typography variant="body2" sx={{ color: isDue(params.value) ? 'error.main' : 'text.primary', paddingTop: 2 }}>
            {formatDate(params.value)}
          </Typography>
          {isDue(params.value) && (
            <Chip label="Overdue" size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />
          )}
        </Stack>
      ),
    },
    {
      field: 'problems', headerName: 'Problems', width: 100, align: 'center', headerAlign: 'center',
      renderCell: (params) => <Chip label={params.value?.length ?? 0} size="small" sx={primaryChipSx} />,
    },
    // ── Progress column — reads pre-computed API fields, no client recomputation ──
    {
      field: 'progressPercentage',
      headerName: 'Progress',
      width: 170,
      sortable: true,
      // valueGetter returns the raw numeric pct for DataGrid sorting
      // Dual-signature shim: v6 passes (value, row), v5 passes (params)
      valueGetter: (valueOrParams, row) => {
        const r = row ?? valueOrParams?.row;
        return r?.progressPercentage ?? 0;
      },
      renderCell: (params) => {
        const pct      = params.row.progressPercentage      ?? 0;
        const solved   = params.row.progressSolvedCount     ?? 0;
        const expected = params.row.progressExpectedCount   ?? 0;

        if (expected === 0) {
          return (
            <Typography variant="body2" color="text.disabled">
              —
            </Typography>
          );
        }

        return (
          <Box sx={{ width: '100%', pr: 1 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="baseline"
              sx={{ mb: 0.5 }}
            >
              <Typography
                variant="caption"
                fontWeight={700}
                sx={{ color: 'text.primary', lineHeight: 1 }}
              >
                {pct}%
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontSize: '0.68rem', lineHeight: 1 }}
              >
                {solved}&thinsp;/&thinsp;{expected}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={Math.min(pct, 100)}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'action.hover',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  bgcolor: 'primary.main',
                  opacity: pct === 0 ? 0 : 1,
                },
              }}
            />
          </Box>
        );
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 150, sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} sx={{ paddingTop: 2 }}>
          <Tooltip title="View Details">
            <Button size="small" onClick={() => navigate(cr.assignment(batchId, params.row._id))}
              sx={{ minWidth: 36, color: PRIMARY }}>
              <VisibilityRounded fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="View Progress">
            <Button size="small"
              onClick={() => navigate(cr.assignmentProgress(batchId, params.row._id))}
              sx={{ minWidth: 36, color: SECONDARY }}>
              <HourglassBottomOutlined fontSize="small" />
            </Button>
          </Tooltip>
          <Tooltip title="Delete">
            <Button size="small" color="error" disabled={deleting === params.row._id}
              onClick={() => handleDelete(params.row._id)} sx={{ minWidth: 36 }}>
              {deleting === params.row._id ? <CircularProgress size={16} /> : <DeleteRounded fontSize="small" />}
            </Button>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;
  if (error) return <Box p={4}><Alert severity="error">{error}</Alert></Box>;

  return (
    <Box
      sx={{
        p: { xs: 1, md: 2 },
        maxWidth: 1200,
        mx: "auto",
        minHeight: "100vh",
      }}
    >
      <AppBreadcrumbs
        items={[
          { label: "Coding Tracker", path: cr.trackerHome },
          { label: currentBatch?.name || "Batch", path: cr.batch(batchId) },
          { label: "Assignments" },
        ]}
      />

      <GlassCard
        hoverable={false}
        sx={{
          p: { xs: 1.5, md: 2 },
          mb: 3,
          borderRadius: 4,
          ...surfaceGradientSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box>
          <Chip
            label={currentBatch?.code}
            size="small"
            sx={{ mb: 1.5, ...primaryChipSx }}
          />
          <Typography
            variant="h5"
            fontWeight={800}
            sx={{ ...gradientTextSx, mb: 0.5 }}
          >
            Assignments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineRoundedIcon />}
          onClick={() => setShowModal(true)}
          sx={{ ...gradientButtonSx, alignItems: 'center' }}
        >
          Create Assignment
        </Button>
      </GlassCard>

      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
        <Chip
          icon={
            <AssignmentRoundedIcon
              sx={{
                fontSize: "1rem !important",
                color: `${PRIMARY} !important`,
              }}
            />
          }
          label={`${assignments.length} Assignments`}
          sx={primaryChipSx}
        />
      </Stack>

      {assignments.length === 0 ? (
        <GlassCard
          hoverable={false}
          sx={{
            textAlign: "center",
            py: 10,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 4,
          }}
        >
          <Typography variant="h6" color="text.secondary" mb={1}>
            No assignments yet
          </Typography>
          <Typography variant="body2" color="text.disabled" mb={3}>
            Create your first assignment to add problems.
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineRoundedIcon />}
            onClick={() => setShowModal(true)}
            sx={{ ...gradientButtonSx, px: 3 }}
          >
            Create first assignment
          </Button>
        </GlassCard>
      ) : (
        <GlassCard
          hoverable={false}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          <CommonDataGrid
            firstColumnField="index"
            rows={assignments.map((a) => ({ ...a, id: a._id }))}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                ...gridHeaderBgSx,
                fontWeight: 700,
                fontSize: "0.8rem",
                letterSpacing: "0.05em",
                textTransform: "uppercase",
                color: "text.secondary",
              },
              "& .MuiDataGrid-row": { gridRowHoverSx, ...gridRowStripeSx },
              "& .MuiDataGrid-cell": { alignItems: "center" },
            }}
          />
        </GlassCard>
      )}

      {showModal && (
        <AddAssignmentModal
          onClose={() => setShowModal(false)}
          onSubmit={handleCreate}
        />
      )}
    </Box>
  );
};

export default AssignmentPage;
